import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./ProjectManagement.css";
import { BiHomeSmile } from "react-icons/bi";

import {
  FiPlus,
  FiEdit,
  FiTrash2,
  FiEye,
  FiX,
  FiCheck,
  FiUpload,
  FiImage,
  FiMapPin,
  FiHome,
  FiStar,
  FiFileText,
  FiDollarSign,
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiCalendar,
  FiTag,
  FiNavigation,
  FiTrendingUp,
  FiSettings,
  FiBarChart,
  FiType,
  FiAlertTriangle,
  FiRefreshCw,
  FiFilter,
  FiList,
  FiLayers as FiSpecs,
  FiCreditCard,
  FiAward,
  FiVideo,
  FiFile,
  FiBold,
  FiItalic,
  FiUnderline,
  FiList as FiUl,
  FiLink,
} from "react-icons/fi";

// Helper function to safely parse JSON
const safeJSONParse = (jsonString, fallback = []) => {
  if (!jsonString || typeof jsonString !== "string") {
    return fallback;
  }

  try {
    const trimmed = jsonString.trim();
    if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
      return fallback;
    }

    const firstChar = trimmed.charAt(0);
    const lastChar = trimmed.charAt(trimmed.length - 1);

    if (
      (firstChar === "[" && lastChar === "]") ||
      (firstChar === "{" && lastChar === "}")
    ) {
      return JSON.parse(trimmed);
    }

    return fallback;
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return fallback;
  }
};

// Generate unique IDs
const generateId = () => {
  return `id-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Simple text formatting helper functions
const formatText = {
  bold: (text) => `<strong>${text}</strong>`,
  italic: (text) => `<em>${text}</em>`,
  underline: (text) => `<u>${text}</u>`,
  link: (text, url) => `<a href="${url}" target="_blank">${text}</a>`,
  list: (items) =>
    `<ul>${items.map((item) => `<li>${item}</li>`).join("")}</ul>`,
};

// Additional Details Tab Component
const AdditionalDetailsTab = React.memo(
  ({
    areaStatement,
    specificationDetails,
    paymentSchedule,
    highcontent,
    onUpdateAreaStatement,
    onUpdateSpecificationDetail,
    onUpdatePaymentSchedule,
    onUpdateHighcontent,
    onRemoveAreaStatement,
    onRemoveSpecificationDetail,
    onRemovePaymentSchedule,
    onRemoveHighcontent,
    onAddAreaStatement,
    onAddSpecificationDetail,
    onAddPaymentSchedule,
    onAddHighcontent,
  }) => {
    return (
      <div className="features-container">
        {/* Area Statement */}
        <div className="additional-details-section">
          <div className="details-header">
            <h3>
              <BiHomeSmile /> Area Statement
            </h3>
            <button
              type="button"
              onClick={onAddAreaStatement}
              className="add-detail-btn"
            >
              <FiPlus /> Add Row
            </button>
          </div>

          {areaStatement.length > 0 ? (
            <div className="table-responsive">
              <table className="details-table">
                <thead>
                  <tr>
                    <th>Flat Number</th>
                    <th>Super Plinth Area in Sq.Ft</th>
                    <th>BHK</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {areaStatement.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <input
                          type="text"
                          value={row.flat_number || ""}
                          onChange={(e) =>
                            onUpdateAreaStatement(
                              row.id,
                              "flat_number",
                              e.target.value,
                            )
                          }
                          placeholder="e.g., A-101"
                          className="detail-input"
                          autoComplete="off"
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={row.total_sqft || ""}
                          onChange={(e) =>
                            onUpdateAreaStatement(
                              row.id,
                              "total_sqft",
                              e.target.value,
                            )
                          }
                          placeholder="e.g., 1200"
                          className="detail-input"
                          autoComplete="off"
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.bhk || ""}
                          onChange={(e) =>
                            onUpdateAreaStatement(row.id, "bhk", e.target.value)
                          }
                          placeholder="e.g., 2 BHK"
                          className="detail-input"
                          autoComplete="off"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => onRemoveAreaStatement(row.id)}
                          className="remove-row-btn"
                          title="Remove row"
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-details">
              <FiList className="empty-media-icon" />
              <p>No area statement added yet</p>
              <p>Add flat details with square footage and BHK configuration</p>
            </div>
          )}
        </div>

        {/* Two Column Grid for Specifications and Payment Schedule */}
        <div className="two-column-grid">
          {/* Specification Details */}
          <div className="detail-column">
            <div className="details-header">
              <h3>
                <FiSpecs /> Specification Details
              </h3>
              <button
                type="button"
                onClick={onAddSpecificationDetail}
                className="add-detail-btn"
              >
                <FiPlus /> Add Specification
              </button>
            </div>

            {specificationDetails.length > 0 ? (
              <div className="table-responsive">
                <table className="details-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Content ID</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {specificationDetails.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <input
                            type="text"
                            value={row.title || ""}
                            onChange={(e) =>
                              onUpdateSpecificationDetail(
                                row.id,
                                "title",
                                e.target.value,
                              )
                            }
                            placeholder="e.g., Flooring Specifications"
                            className="detail-input"
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={row.content_id || ""}
                            onChange={(e) =>
                              onUpdateSpecificationDetail(
                                row.id,
                                "content_id",
                                e.target.value,
                              )
                            }
                            placeholder="e.g., SPEC001"
                            className="detail-input"
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => onRemoveSpecificationDetail(row.id)}
                            className="remove-row-btn"
                            title="Remove specification"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-details">
                <FiFileText className="empty-media-icon" />
                <p>No specification details added yet</p>
                <p>Add detailed specifications for the project</p>
              </div>
            )}
          </div>

          {/* Payment Schedule */}
          <div className="detail-column">
            <div className="details-header">
              <h3>
                <FiCreditCard /> Payment Schedule
              </h3>
              <button
                type="button"
                onClick={onAddPaymentSchedule}
                className="add-detail-btn"
              >
                <FiPlus /> Add Payment
              </button>
            </div>

            {paymentSchedule.length > 0 ? (
              <div className="table-responsive">
                <table className="details-table">
                  <thead>
                    <tr>
                      <th>Payment Details</th>
                      <th>Amount (%)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentSchedule.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <input
                            type="text"
                            value={row.details || ""}
                            onChange={(e) =>
                              onUpdatePaymentSchedule(
                                row.id,
                                "details",
                                e.target.value,
                              )
                            }
                            placeholder="e.g., On Booking"
                            className="detail-input"
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={row.payment || ""}
                            onChange={(e) =>
                              onUpdatePaymentSchedule(
                                row.id,
                                "payment",
                                e.target.value,
                              )
                            }
                            placeholder="e.g., 10"
                            className="detail-input"
                            autoComplete="off"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => onRemovePaymentSchedule(row.id)}
                            className="remove-row-btn"
                            title="Remove payment"
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-details">
                <FiDollarSign className="empty-media-icon" />
                <p>No payment schedule added yet</p>
                <p>Add payment milestones and percentages</p>
              </div>
            )}
          </div>
        </div>

        {/* High Content Section */}
        <div className="additional-details-section">
          <div className="details-header">
            <h3>
              <FiAward /> Highlights Content
            </h3>
            <button
              type="button"
              onClick={onAddHighcontent}
              className="add-detail-btn"
            >
              <FiPlus /> Add Content
            </button>
          </div>

          {highcontent.length > 0 ? (
            <div className="table-responsive">
              <table className="details-table">
                <thead>
                  <tr>
                    <th>Content</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {highcontent.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <input
                          type="text"
                          value={row.value || ""}
                          onChange={(e) =>
                            onUpdateHighcontent(row.id, "value", e.target.value)
                          }
                          placeholder="Enter high content value..."
                          className="detail-input"
                          autoComplete="off"
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => onRemoveHighcontent(row.id)}
                          className="remove-row-btn"
                          title="Remove content"
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-details">
              <FiFileText className="empty-media-icon" />
              <p>No high content added yet</p>
              <p>Add important content for the project</p>
            </div>
          )}
        </div>
      </div>
    );
  },
);

// Rich Text Editor Component
const RichTextEditor = ({ value, onChange, placeholder, rows = 6 }) => {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const textareaRef = useRef(null);

  const handleFormat = (format) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);

    let newText = value;
    let newCursorPos = end;

    switch (format) {
      case "bold":
        newText =
          value.substring(0, start) +
          `<strong>${selectedText}</strong>` +
          value.substring(end);
        newCursorPos = start + 8 + selectedText.length + 9;
        break;

      case "italic":
        newText =
          value.substring(0, start) +
          `<em>${selectedText}</em>` +
          value.substring(end);
        newCursorPos = start + 4 + selectedText.length + 5;
        break;

      case "underline":
        newText =
          value.substring(0, start) +
          `<u>${selectedText}</u>` +
          value.substring(end);
        newCursorPos = start + 3 + selectedText.length + 4;
        break;

      case "list":
        const items = selectedText.split("\n").filter((item) => item.trim());
        const listHTML = `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`;
        newText = value.substring(0, start) + listHTML + value.substring(end);
        newCursorPos = start + listHTML.length;
        break;

      case "link":
        setLinkText(selectedText);
        setIsLinkModalOpen(true);
        return;
    }

    onChange(newText);

    setTimeout(() => {
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  const handleAddLink = () => {
    if (!linkUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = linkText.trim() || "Link";

    const linkHTML = `<a href="${linkUrl}" target="_blank">${selectedText}</a>`;
    const newText = value.substring(0, start) + linkHTML + value.substring(end);

    onChange(newText);
    setIsLinkModalOpen(false);
    setLinkText("");
    setLinkUrl("");

    setTimeout(() => {
      const newCursorPos = start + linkHTML.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  return (
    <div className="rich-text-editor-container">
      <div className="text-formatting-toolbar">
        <button
          type="button"
          onClick={() => handleFormat("bold")}
          className="format-btn"
          title="Bold"
        >
          <FiBold />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("italic")}
          className="format-btn"
          title="Italic"
        >
          <FiItalic />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("underline")}
          className="format-btn"
          title="Underline"
        >
          <FiUnderline />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("list")}
          className="format-btn"
          title="Bullet List"
        >
          <FiUl />
        </button>
        <button
          type="button"
          onClick={() => handleFormat("link")}
          className="format-btn"
          title="Insert Link"
        >
          <FiLink />
        </button>
      </div>

      <div className="editor-wrapper">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          className="editor-textarea"
        />

        {/* Preview Section */}
        <div className="editor-preview">
          <div className="preview-label">Preview:</div>
          <div
            className="preview-content"
            dangerouslySetInnerHTML={{
              __html:
                value ||
                `<span class="preview-placeholder">${placeholder}</span>`,
            }}
          />
        </div>
      </div>

      {isLinkModalOpen && (
        <div className="link-modal-overlay">
          <div className="link-modal">
            <div className="link-modal-header">
              <h3>Insert Link</h3>
              <button
                type="button"
                onClick={() => setIsLinkModalOpen(false)}
                className="close-modal-btn"
              >
                &times;
              </button>
            </div>
            <div className="link-modal-body">
              <div className="form-group">
                <label>Link Text</label>
                <input
                  type="text"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Text to display"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>URL *</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://example.com"
                  required
                  className="form-input"
                />
              </div>
              <div className="link-modal-actions">
                <button
                  type="button"
                  onClick={() => setIsLinkModalOpen(false)}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddLink}
                  className="submit-btn"
                  disabled={!linkUrl.trim()}
                >
                  Insert Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectManagementDataTable = () => {
  // States
  const [activeTab, setActiveTab] = useState("basic");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingProject, setViewingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [amenities, setAmenities] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [nearbyLocations, setNearbyLocations] = useState([]);
  const [categories, setCategories] = useState([]);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Form data with new fields for brochure and video
  const [formData, setFormData] = useState({
    category_id: "",
    project_name: "",
    project_caption: "",
    project_overview: "",
    location: "",
    get_direction: "",
    project_status: "",
    project_process_stage: "",
    amenities: "[]",
    specifications: "[]",
    highlights: "[]",
    nearby_locations: "[]",
    area_statement: "[]",
    specification_details: "[]",
    payment_schedule: "[]",
    highcontent: "[]",
    rank: "",
    brochure: "",
    video: "",
  });

  // File states
  const [coverPreview, setCoverPreview] = useState("");
  const [bannerPreview, setBannerPreview] = useState("");
  const [homeImagePreview, setHomeImagePreview] = useState("");
  const [brochurePreview, setBrochurePreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [homeImageFile, setHomeImageFile] = useState(null);
  const [brochureFile, setBrochureFile] = useState(null);
  const [floorplanFiles, setFloorplanFiles] = useState([]);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [floorplanNames, setFloorplanNames] = useState([]);
  const [galleryNames, setGalleryNames] = useState([]);
  const [highlightInput, setHighlightInput] = useState("");
  const [highlightType, setHighlightType] = useState("text");
  const [currentBatchName, setCurrentBatchName] = useState("Gallery 1");
  const [currentBatchFiles, setCurrentBatchFiles] = useState([]);

  // Additional details states with ref for debouncing
  const [areaStatement, setAreaStatement] = useState([]);
  const [specificationDetails, setSpecificationDetails] = useState([]);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [highcontent, setHighcontent] = useState([]);

  // Deletion tracking for backend
  const [deleteFloorplans, setDeleteFloorplans] = useState([]);
  const [deleteGalleryBlocks, setDeleteGalleryBlocks] = useState([]);
  const [deleteGalleryImages, setDeleteGalleryImages] = useState([]);
  const [deleteHighcontent, setDeleteHighcontent] = useState([]);
  const [deleteBrochure, setDeleteBrochure] = useState(false);

  // Ref for debouncing form updates
  const formUpdateTimeoutRef = useRef(null);

  // API Configuration
  const API_BASE = "https://dev.mithahomes.com/backend/api/mainprojectprocess";
  const ImgURL = "https://dev.mithahomes.com/backend";

  // Status options
  const projectStatuses = [
    { id: "planning", name: "Planning", percentage: 10, color: "#f59e0b" },
    { id: "design", name: "Design", percentage: 25, color: "#3b82f6" },
    {
      id: "construction",
      name: "Under Construction",
      percentage: 50,
      color: "#8b5cf6",
    },
    { id: "finishing", name: "Finishing", percentage: 75, color: "#10b981" },
    { id: "ready", name: "Ready to Move", percentage: 90, color: "#84cc16" },
    { id: "completed", name: "Completed", percentage: 100, color: "#06b6d4" },
  ];

  const projectStages = [
    { id: 1, name: "Pre-launch" },
    { id: 2, name: "Launch Phase" },
    { id: 3, name: "Construction Phase" },
    { id: 4, name: "Handover Phase" },
    { id: 5, name: "Post-handover" },
  ];

  const highlightTypes = [{ id: "text", name: "Text", icon: <FiType /> }];

  // ==================== MISSING FUNCTION DEFINITIONS ====================

  // Parse highlights from database
  const parseHighlightsFromDB = (project) => {
    if (!project) return [];

    try {
      if (!project.highlights) return [];

      let highlights = [];

      if (typeof project.highlights === "string") {
        const trimmed = project.highlights.trim();
        if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
          return [];
        }

        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            highlights = parsed;
          } else if (typeof parsed === "object" && parsed !== null) {
            highlights = [parsed];
          }
        } catch (parseError) {
          const items = trimmed
            .split(/[,;\n]/)
            .filter((item) => item.trim() !== "");
          highlights = items.map((item) => ({
            id: generateId(),
            text: item.trim(),
            type: "text",
            value: item.trim(),
          }));
        }
      } else if (Array.isArray(project.highlights)) {
        highlights = project.highlights;
      }

      return highlights
        .map((h, index) => {
          if (typeof h === "object" && h !== null) {
            return {
              id: h.id || generateId(),
              text: h.text || h.value || "",
              type: h.type || "text",
              value: h.value || h.text || "",
            };
          } else {
            return {
              id: generateId(),
              text: String(h),
              type: "text",
              value: String(h),
            };
          }
        })
        .filter((h) => h.value && h.value.trim() !== "");
    } catch (error) {
      console.error("Error parsing highlights:", error);
      return [];
    }
  };

  // Get parsed highlights
  const getParsedHighlights = () => {
    try {
      const jsonString = formData.highlights || "[]";
      const trimmed = jsonString.trim();

      if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
        return [];
      }

      const parsed = JSON.parse(trimmed);
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed;
    } catch (error) {
      console.error("Error in getParsedHighlights:", error);
      return [];
    }
  };

  // Show delete confirmation modal
  const showDeleteConfirmation = (project) => {
    if (!project) {
      toast.error("❌ Invalid project data");
      return;
    }
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  // Delete project
  const handleDelete = async (id) => {
    if (!id) {
      toast.error("❌ Invalid project ID");
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE}/project/${id}`);

      if (response.data.success) {
        toast.success("🗑️ Project deleted successfully!");
        setShowDeleteModal(false);
        setProjectToDelete(null);
        fetchAllData();
      } else {
        toast.error(
          `❌ ${response.data.error || response.data.message || "Error deleting project"}`,
        );
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("❌ Error deleting project. Please try again.");
    }
  };

  // ==================== ADDITIONAL DETAILS FUNCTIONS ====================

  // Helper function to update form data
  const updateFormDataField = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: JSON.stringify(value),
    }));
  }, []);

  // Debounced form data update
  const debouncedFormUpdate = useCallback(
    (field, value) => {
      if (formUpdateTimeoutRef.current) {
        clearTimeout(formUpdateTimeoutRef.current);
      }

      formUpdateTimeoutRef.current = setTimeout(() => {
        updateFormDataField(field, value);
      }, 500);
    },
    [updateFormDataField],
  );

  // Add Area Statement Row
  const addAreaStatementRow = useCallback(() => {
    const newRow = {
      id: generateId(),
      flat_number: "",
      total_sqft: "",
      bhk: "",
    };

    setAreaStatement((prev) => [...prev, newRow]);
    debouncedFormUpdate("area_statement", [...areaStatement, newRow]);
  }, [areaStatement, debouncedFormUpdate]);

  // Update Area Statement
  const updateAreaStatement = useCallback(
    (id, field, value) => {
      setAreaStatement((prev) => {
        const updated = prev.map((row) =>
          row.id === id ? { ...row, [field]: value } : row,
        );
        debouncedFormUpdate("area_statement", updated);
        return updated;
      });
    },
    [debouncedFormUpdate],
  );

  // Remove Area Statement Row
  const removeAreaStatementRow = useCallback(
    (id) => {
      setAreaStatement((prev) => {
        const updated = prev.filter((row) => row.id !== id);
        updateFormDataField("area_statement", updated);
        return updated;
      });
    },
    [updateFormDataField],
  );

  // Add Specification Detail
  const addSpecificationDetail = useCallback(() => {
    const newRow = {
      id: generateId(),
      title: "",
      content_id: "",
    };

    setSpecificationDetails((prev) => [...prev, newRow]);
    debouncedFormUpdate("specification_details", [
      ...specificationDetails,
      newRow,
    ]);
  }, [specificationDetails, debouncedFormUpdate]);

  // Update Specification Detail
  const updateSpecificationDetail = useCallback(
    (id, field, value) => {
      setSpecificationDetails((prev) => {
        const updated = prev.map((row) =>
          row.id === id ? { ...row, [field]: value } : row,
        );
        debouncedFormUpdate("specification_details", updated);
        return updated;
      });
    },
    [debouncedFormUpdate],
  );

  // Remove Specification Detail
  const removeSpecificationDetail = useCallback(
    (id) => {
      setSpecificationDetails((prev) => {
        const updated = prev.filter((row) => row.id !== id);
        updateFormDataField("specification_details", updated);
        return updated;
      });
    },
    [updateFormDataField],
  );

  // Add Payment Schedule Row
  const addPaymentScheduleRow = useCallback(() => {
    const newRow = {
      id: generateId(),
      details: "",
      payment: "",
    };

    setPaymentSchedule((prev) => [...prev, newRow]);
    debouncedFormUpdate("payment_schedule", [...paymentSchedule, newRow]);
  }, [paymentSchedule, debouncedFormUpdate]);

  // Update Payment Schedule
  const updatePaymentSchedule = useCallback(
    (id, field, value) => {
      setPaymentSchedule((prev) => {
        const updated = prev.map((row) =>
          row.id === id ? { ...row, [field]: value } : row,
        );
        debouncedFormUpdate("payment_schedule", updated);
        return updated;
      });
    },
    [debouncedFormUpdate],
  );

  // Remove Payment Schedule Row
  const removePaymentScheduleRow = useCallback(
    (id) => {
      setPaymentSchedule((prev) => {
        const updated = prev.filter((row) => row.id !== id);
        updateFormDataField("payment_schedule", updated);
        return updated;
      });
    },
    [updateFormDataField],
  );

  // Add High Content Row
  const addHighcontentRow = useCallback(() => {
    const newRow = {
      id: generateId(),
      value: "",
    };

    setHighcontent((prev) => [...prev, newRow]);
    debouncedFormUpdate("highcontent", [...highcontent, newRow]);
  }, [highcontent, debouncedFormUpdate]);

  // Update High Content
  const updateHighcontent = useCallback(
    (id, field, value) => {
      setHighcontent((prev) => {
        const updated = prev.map((row) =>
          row.id === id ? { ...row, [field]: value } : row,
        );
        debouncedFormUpdate("highcontent", updated);
        return updated;
      });
    },
    [debouncedFormUpdate],
  );

  // Remove High Content Row
  const removeHighcontentRow = useCallback(
    (id) => {
      setHighcontent((prev) => {
        const updated = prev.filter((row) => row.id !== id);
        updateFormDataField("highcontent", updated);
        return updated;
      });
      setDeleteHighcontent((prev) => [...prev, id]);
    },
    [updateFormDataField],
  );

  // ==================== FLOORPLAN FUNCTIONS ====================

  // Update floorplan name
  const updateFloorplanName = (index, newName) => {
    const updated = [...floorplanNames];

    if (index >= 0 && index < updated.length) {
      updated[index] = {
        ...updated[index],
        name: newName.trim() || `Floorplan ${index + 1}`,
      };
      setFloorplanNames(updated);
    }
  };

  // Remove floorplan
  const removeFloorplan = (index) => {
    const newFloorplanNames = [...floorplanNames];
    const floorplanToRemove = newFloorplanNames[index];

    if (floorplanToRemove.isNew && floorplanToRemove.isFile) {
      const newFloorplanFiles = floorplanFiles.filter(
        (file) => file !== floorplanToRemove.image,
      );
      setFloorplanFiles(newFloorplanFiles);

      if (floorplanToRemove.preview) {
        URL.revokeObjectURL(floorplanToRemove.preview);
      }
    } else if (!floorplanToRemove.isNew) {
      setDeleteFloorplans((prev) => [...prev, floorplanToRemove.id]);
    }

    newFloorplanNames.splice(index, 1);
    setFloorplanNames(newFloorplanNames);

    toast.success("✅ Floorplan removed");
  };

  // ==================== GALLERY FUNCTIONS ====================

  // Delete gallery group
  const deleteGalleryGroup = (groupName, groupId) => {
    const newGalleryNames = galleryNames.filter((item) => item.id !== groupId);
    const removedItems = galleryNames.filter((item) => item.id === groupId);

    const newGalleryFiles = galleryFiles.filter((file) => {
      return !removedItems.some((item) => item.isNew && item.image === file);
    });

    removedItems.forEach((item) => {
      if (item.preview && item.isNew) {
        URL.revokeObjectURL(item.preview);
      }
    });

    const existingItems = removedItems.filter((item) => !item.isNew);
    if (existingItems.length > 0) {
      setDeleteGalleryBlocks((prev) => [...prev, existingItems[0].id]);
    }

    setGalleryNames(newGalleryNames);
    setGalleryFiles(newGalleryFiles);
    toast.success(`✅ Gallery group "${groupName}" removed`);
  };

  // Update gallery name
  const updateGalleryName = (index, newName) => {
    const updated = [...galleryNames];

    if (index >= 0 && index < updated.length) {
      const galleryItem = updated[index];
      const newNameValue =
        newName.trim() || `Gallery ${Math.floor(index / 10) + 1}`;

      updated[index] = {
        ...galleryItem,
        name: newNameValue,
      };

      if (!galleryItem.isNew) {
        const groupId = galleryItem.id;
        updated.forEach((item, idx) => {
          if (item.id === groupId) {
            updated[idx] = {
              ...updated[idx],
              name: newNameValue,
            };
          }
        });
      }

      setGalleryNames(updated);
    }
  };

  // Remove file from gallery
  const removeGalleryImage = (index) => {
    const newGalleryNames = [...galleryNames];
    const galleryItem = newGalleryNames[index];

    if (galleryItem.isNew && galleryItem.image instanceof File) {
      const newGalleryFiles = galleryFiles.filter(
        (file) => file !== galleryItem.image,
      );
      setGalleryFiles(newGalleryFiles);

      if (galleryItem.preview) {
        URL.revokeObjectURL(galleryItem.preview);
      }
    } else if (!galleryItem.isNew) {
      const galleryBlock = galleryNames.find((g) => g.id === galleryItem.id);
      if (galleryBlock) {
        const imagesInBlock = galleryNames.filter(
          (g) => g.id === galleryItem.id,
        );

        if (imagesInBlock.length === 1) {
          setDeleteGalleryBlocks((prev) => [...prev, galleryItem.id]);
        } else {
          const imageUrl = galleryItem.url || galleryItem.image;
          setDeleteGalleryImages((prev) => {
            const existing = prev.find((p) => p.gid === galleryItem.id);
            if (existing) {
              return prev.map((p) =>
                p.gid === galleryItem.id
                  ? { ...p, images: [...p.images, imageUrl] }
                  : p,
              );
            } else {
              return [...prev, { gid: galleryItem.id, images: [imageUrl] }];
            }
          });
        }
      }
    }

    newGalleryNames.splice(index, 1);
    setGalleryNames(newGalleryNames);
    toast.success("✅ Gallery image removed");
  };

  // ==================== OTHER FUNCTIONS ====================

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      try {
        const projectsRes = await axios.get(`${API_BASE}/projects/sampletes`);

        if (projectsRes.data.success && projectsRes.data.projects) {
          setProjects(projectsRes.data.projects);
          console.log("Projects loaded:", projectsRes.data.projects.length);
        } else {
          toast.error("❌ Unexpected API response structure");
          setProjects([]);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
        toast.error("❌ Error loading projects");
        setProjects([]);
      }

      try {
        const catsRes = await axios.get(
          `https://dev.mithahomes.com/backend/api/project/all`,
        );
        if (catsRes.data.success) {
          const activeCategories = catsRes.data.data.filter(
            (c) => c && (c.status === "active" || !c.status),
          );
          setCategories(activeCategories);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }

      try {
        const amenitiesRes = await axios.get(
          `https://dev.mithahomes.com/backend/api/amenities/all`,
        );
        if (amenitiesRes.data.success) {
          const activeAmenities = amenitiesRes.data.data.filter(
            (a) => a && (a.status === "active" || !a.status),
          );
          setAmenities(activeAmenities);
        }
      } catch (error) {
        console.error("Error fetching amenities:", error);
      }

      try {
        const specsRes = await axios.get(
          `https://dev.mithahomes.com/backend/api/specification/all`,
        );
        if (specsRes.data.success) {
          const activeSpecs = specsRes.data.data.filter(
            (s) => s && (s.status === "active" || !s.status),
          );
          setSpecifications(activeSpecs);
        }
      } catch (error) {
        console.error("Error fetching specifications:", error);
      }

      try {
        const nearbyRes = await axios.get(
          `https://dev.mithahomes.com/backend/api/nearbylocation/all`,
        );
        if (nearbyRes.data.success) {
          const activeLocations = nearbyRes.data.data.filter(
            (n) => n && (n.status === "active" || !n.status),
          );
          setNearbyLocations(activeLocations);
        }
      } catch (error) {
        console.error("Error fetching nearby locations:", error);
      }
    } catch (error) {
      console.error("Error in fetchAllData:", error);
      toast.error("❌ Error loading data");
    } finally {
      setLoading(false);
    }
  };

  // Refresh data function
  const refreshData = () => {
    fetchAllData();
    toast.info("🔄 Refreshing data...");
  };

  // Form handlers
  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Handle textarea change for project overview
  const handleTextareaChange = useCallback((e) => {
    setFormData((prev) => ({ ...prev, project_overview: e.target.value }));
  }, []);

  // File upload handler
  const handleFileUpload = (e, field) => {
    const files = Array.from(e.target.files);

    if (field === "cover_image") {
      const file = files[0];
      setCoverFile(file);
      setCoverPreview(URL.createObjectURL(file));
    } else if (field === "banner_image") {
      const file = files[0];
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    } else if (field === "home_images") {
      const file = files[0];
      setHomeImageFile(file);
      setHomeImagePreview(URL.createObjectURL(file));
    } else if (field === "brochure") {
      const file = files[0];
      if (file) {
        const validTypes = [
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.ms-excel",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];

        if (!validTypes.includes(file.type)) {
          toast.error(
            "❌ Please upload a valid document (PDF, DOC, DOCX, XLS, XLSX)",
          );
          return;
        }

        setBrochureFile(file);
        setBrochurePreview({
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file),
        });
        setDeleteBrochure(false);
      }
    } else if (field === "floorplans") {
      const newFloorplanNames = [...floorplanNames];
      const newFloorplanFiles = [...floorplanFiles];

      files.forEach((file, index) => {
        const uniqueId = generateId();
        newFloorplanFiles.push(file);
        newFloorplanNames.push({
          id: uniqueId,
          name: `Floorplan ${floorplanNames.length + index + 1}`,
          image: file,
          isNew: true,
          isFile: true,
          preview: URL.createObjectURL(file),
          tempName: file.name,
        });
      });

      setFloorplanNames(newFloorplanNames);
      setFloorplanFiles(newFloorplanFiles);
    } else if (field === "gallery_batch") {
      if (files.length > 10) {
        toast.error("❌ Maximum 10 images per batch");
        return;
      }
      setCurrentBatchFiles(files.slice(0, 10));
    }

    e.target.value = "";
  };

  const addBatchToGallery = () => {
    if (currentBatchFiles.length === 0) {
      toast.error("❌ No images in current batch");
      return;
    }

    const batchName =
      currentBatchName.trim() ||
      `Gallery ${Math.floor(galleryNames.length / 10) + 1}`;
    const batchId = generateId();

    const newGalleryNames = [...galleryNames];
    const newGalleryFiles = [...galleryFiles];

    currentBatchFiles.forEach((file, index) => {
      newGalleryFiles.push(file);
      newGalleryNames.push({
        id: batchId,
        name: batchName,
        image: file,
        isNew: true,
        isFile: true,
        preview: URL.createObjectURL(file),
        tempName: file.name,
      });
    });

    setGalleryNames(newGalleryNames);
    setGalleryFiles(newGalleryFiles);
    setCurrentBatchFiles([]);
    setCurrentBatchName(
      `Gallery ${Math.floor(newGalleryNames.length / 10) + 2}`,
    );
    toast.success(
      `✅ ${currentBatchFiles.length} images added to "${batchName}"`,
    );
  };

  // Remove brochure
  const removeBrochure = () => {
    if (brochurePreview && brochurePreview.url) {
      URL.revokeObjectURL(brochurePreview.url);
    }
    setBrochurePreview(null);
    setBrochureFile(null);
    setDeleteBrochure(true);
    setFormData((prev) => ({ ...prev, brochure: "" }));
  };

  // Checkbox toggle function
  const handleFeatureToggle = (type, id) => {
    setFormData((prev) => {
      const currentArray = safeJSONParse(prev[type] || "[]", []);
      let newArray;

      const idToCompare = String(id);

      const exists = currentArray.some(
        (item) =>
          String(item) === idToCompare ||
          (typeof item === "object" &&
            item.id &&
            String(item.id) === idToCompare),
      );

      if (exists) {
        newArray = currentArray.filter(
          (item) =>
            !(
              String(item) === idToCompare ||
              (typeof item === "object" &&
                item.id &&
                String(item.id) === idToCompare)
            ),
        );
      } else {
        const idNum = Number(id);
        newArray = [...currentArray, isNaN(idNum) ? id : idNum];
      }

      return { ...prev, [type]: JSON.stringify(newArray) };
    });
  };

  const handleAddHighlight = () => {
    if (!highlightInput.trim()) {
      toast.error("❌ Please enter highlight text");
      return;
    }

    const currentHighlights = getParsedHighlights();
    const newHighlight = {
      id: generateId(),
      text: highlightInput.trim(),
      type: highlightType,
      value: highlightInput.trim(),
    };

    const updatedHighlights = [...currentHighlights, newHighlight];

    setFormData((prev) => ({
      ...prev,
      highlights: JSON.stringify(updatedHighlights),
    }));

    setHighlightInput("");
    toast.success("✅ Highlight added");
  };

  const removeHighlight = (id) => {
    const currentHighlights = getParsedHighlights();
    const updatedHighlights = currentHighlights.filter((h) => h.id !== id);
    setFormData((prev) => ({
      ...prev,
      highlights: JSON.stringify(updatedHighlights),
    }));
  };

  // Parse additional details when editing a project with null checks
  useEffect(() => {
    if (editingProject) {
      try {
        if (editingProject.area_statement) {
          let areaData = [];
          if (typeof editingProject.area_statement === "string") {
            const trimmed = editingProject.area_statement.trim();
            if (trimmed && trimmed !== "null" && trimmed !== "undefined") {
              try {
                areaData = JSON.parse(trimmed);
              } catch (e) {
                console.warn("Failed to parse area_statement:", e);
              }
            }
          } else if (Array.isArray(editingProject.area_statement)) {
            areaData = editingProject.area_statement;
          }

          const formattedAreaData = areaData.map((item, index) => ({
            id: item?.id || generateId(),
            flat_number: item?.flat_number || "",
            total_sqft: item?.total_sqft || "",
            bhk: item?.bhk || "",
          }));

          setAreaStatement(formattedAreaData);
          updateFormDataField("area_statement", formattedAreaData);
        } else {
          setAreaStatement([]);
          updateFormDataField("area_statement", []);
        }
      } catch (error) {
        console.error("Error parsing area statement:", error);
        setAreaStatement([]);
        updateFormDataField("area_statement", []);
      }

      try {
        if (editingProject.specification_details) {
          let specData = [];
          if (typeof editingProject.specification_details === "string") {
            const trimmed = editingProject.specification_details.trim();
            if (trimmed && trimmed !== "null" && trimmed !== "undefined") {
              try {
                specData = JSON.parse(trimmed);
              } catch (e) {
                console.warn("Failed to parse specification_details:", e);
              }
            }
          } else if (Array.isArray(editingProject.specification_details)) {
            specData = editingProject.specification_details;
          }

          const formattedSpecData = specData.map((item, index) => ({
            id: item?.id || generateId(),
            title: item?.title || "",
            content_id: item?.content_id || "",
          }));

          setSpecificationDetails(formattedSpecData);
          updateFormDataField("specification_details", formattedSpecData);
        } else {
          setSpecificationDetails([]);
          updateFormDataField("specification_details", []);
        }
      } catch (error) {
        console.error("Error parsing specification details:", error);
        setSpecificationDetails([]);
        updateFormDataField("specification_details", []);
      }

      try {
        if (editingProject.payment_schedule) {
          let paymentData = [];
          if (typeof editingProject.payment_schedule === "string") {
            const trimmed = editingProject.payment_schedule.trim();
            if (trimmed && trimmed !== "null" && trimmed !== "undefined") {
              try {
                paymentData = JSON.parse(trimmed);
              } catch (e) {
                console.warn("Failed to parse payment_schedule:", e);
              }
            }
          } else if (Array.isArray(editingProject.payment_schedule)) {
            paymentData = editingProject.payment_schedule;
          }

          const formattedPaymentData = paymentData.map((item, index) => ({
            id: item?.id || generateId(),
            details: item?.details || "",
            payment: item?.payment || "",
          }));

          setPaymentSchedule(formattedPaymentData);
          updateFormDataField("payment_schedule", formattedPaymentData);
        } else {
          setPaymentSchedule([]);
          updateFormDataField("payment_schedule", []);
        }
      } catch (error) {
        console.error("Error parsing payment schedule:", error);
        setPaymentSchedule([]);
        updateFormDataField("payment_schedule", []);
      }

      try {
        if (editingProject.highcontent) {
          let highcontentData = [];
          if (typeof editingProject.highcontent === "string") {
            const trimmed = editingProject.highcontent.trim();
            if (trimmed && trimmed !== "null" && trimmed !== "undefined") {
              try {
                highcontentData = JSON.parse(trimmed);
              } catch (e) {
                console.warn("Failed to parse highcontent:", e);
              }
            }
          } else if (Array.isArray(editingProject.highcontent)) {
            highcontentData = editingProject.highcontent;
          }

          const formattedHighcontentData = highcontentData.map(
            (item, index) => ({
              id: item?.id || generateId(),
              value: item?.value || "",
            }),
          );

          setHighcontent(formattedHighcontentData);
          updateFormDataField("highcontent", formattedHighcontentData);
        } else {
          setHighcontent([]);
          updateFormDataField("highcontent", []);
        }
      } catch (error) {
        console.error("Error parsing high content:", error);
        setHighcontent([]);
        updateFormDataField("highcontent", []);
      }

      if (editingProject.rank !== undefined) {
        setFormData((prev) => ({
          ...prev,
          rank: editingProject.rank || "",
        }));
      }
    }
  }, [editingProject, updateFormDataField]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (formUpdateTimeoutRef.current) {
        clearTimeout(formUpdateTimeoutRef.current);
      }
    };
  }, []);

  // Edit project with null checks
  const handleEdit = (project) => {
    if (!project) {
      toast.error("❌ Invalid project data");
      return;
    }

    console.log("Editing project video:", project.video);
    setEditingProject(project);

    setDeleteFloorplans([]);
    setDeleteGalleryBlocks([]);
    setDeleteGalleryImages([]);
    setDeleteHighcontent([]);
    setDeleteBrochure(false);

    const parseIdsFromDB = (fieldName) => {
      try {
        if (!project[fieldName]) return [];

        const value = project[fieldName];

        if (typeof value === "string") {
          const trimmed = value.trim();
          if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
            return [];
          }

          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
              return parsed
                .map((item) => {
                  if (typeof item === "object" && item !== null) {
                    const id =
                      item.id ||
                      item.amid ||
                      item.sid ||
                      item.nid ||
                      item.category_id;
                    return id ? Number(id) : null;
                  }
                  return typeof item === "number" ? item : Number(item);
                })
                .filter((id) => id !== null && !isNaN(id));
            }
            return [];
          } catch (parseError) {
            console.warn(`Field ${fieldName} is not valid JSON:`, value);
            return [];
          }
        }

        if (Array.isArray(value)) {
          return value
            .map((item) => {
              if (typeof item === "object" && item !== null) {
                const id =
                  item.id ||
                  item.amid ||
                  item.sid ||
                  item.nid ||
                  item.category_id;
                return id ? Number(id) : null;
              }
              return typeof item === "number" ? item : Number(item);
            })
            .filter((id) => id !== null && !isNaN(id));
        }

        return [];
      } catch (error) {
        console.error(`Error parsing ${fieldName}:`, error);
        return [];
      }
    };

    const highlightsData = parseHighlightsFromDB(project);

    const amenitiesData = parseIdsFromDB("amenities");
    const specificationsData = parseIdsFromDB("specifications");
    const nearbyLocationsData = parseIdsFromDB("nearby_locations");

    // Handle video URL - convert null/undefined to empty string
    const videoUrl =
      project.video === null ||
      project.video === undefined ||
      project.video === "null"
        ? ""
        : project.video;

    setFormData({
      category_id: project.category_id ? String(project.category_id) : "",
      project_name: project.project_name || "",
      project_caption: project.project_caption || "",
      project_overview: project.project_overview || "",
      location: project.location || "",
      get_direction: project.get_direction || "",
      project_status: project.project_status || "",
      project_process_stage: project.project_process_stage || "",
      amenities: JSON.stringify(amenitiesData),
      specifications: JSON.stringify(specificationsData),
      highlights: JSON.stringify(highlightsData),
      nearby_locations: JSON.stringify(nearbyLocationsData),
      area_statement: project.area_statement || "[]",
      specification_details: project.specification_details || "[]",
      payment_schedule: project.payment_schedule || "[]",
      highcontent: project.highcontent || "[]",
      rank: project.rank || "",
      brochure: project.brochure || "",
      video: videoUrl,
    });

    const setImagePreview = (imageField, setPreview) => {
      if (project[imageField]) {
        const fullUrl = project[imageField].startsWith("http")
          ? project[imageField]
          : `${ImgURL}${project[imageField]}`;
        setPreview(fullUrl);
      } else {
        setPreview("");
      }
    };

    setImagePreview("cover_image", setCoverPreview);
    setImagePreview("banner_image", setBannerPreview);
    setImagePreview("home_image", setHomeImagePreview);

    if (project.brochure) {
      const fullUrl = project.brochure.startsWith("http")
        ? project.brochure
        : project.brochure.startsWith("/uploads/")
          ? `${ImgURL}${project.brochure}`
          : project.brochure;

      setBrochurePreview({
        name: project.brochure.split("/").pop() || "Brochure",
        url: fullUrl,
        isExisting: true,
      });
    } else {
      setBrochurePreview(null);
    }

    const parsedFloorplans = [];
    try {
      let floorplans = [];

      if (project.floorplans) {
        if (typeof project.floorplans === "string") {
          floorplans = safeJSONParse(project.floorplans, []);
        } else if (Array.isArray(project.floorplans)) {
          floorplans = project.floorplans;
        }
      }

      console.log("Raw floorplans:", floorplans);

      const seenIds = new Set();
      const seenNames = new Set();

      floorplans.forEach((fp, index) => {
        if (!fp) return;

        const fpId = fp.id || fp.floorplan_id || generateId();
        let name = fp.name || fp.floorplan_name || `Floorplan ${index + 1}`;

        let imagePath = "";
        if (typeof fp === "string") {
          imagePath = fp;
        } else if (fp.image) {
          imagePath = fp.image;
        } else if (fp.image_url) {
          imagePath = fp.image_url;
        } else if (fp.url) {
          imagePath = fp.url;
        }

        if (!imagePath) return;

        if (seenIds.has(fpId) || seenNames.has(name)) {
          console.warn("Duplicate floorplan found, skipping:", { fpId, name });
          return;
        }

        seenIds.add(fpId);
        seenNames.add(name);

        const fullUrl = imagePath.startsWith("http")
          ? imagePath
          : imagePath
            ? `${ImgURL}${imagePath}`
            : "";

        parsedFloorplans.push({
          id: fpId,
          name: name,
          image: imagePath,
          isNew: false,
          isFile: false,
          url: fullUrl,
          preview: fullUrl,
          originalIndex: index,
        });
      });
    } catch (error) {
      console.error("Error parsing floorplans:", error);
    }

    const parsedGallery = [];
    try {
      let galleryData = [];

      if (project.gallery) {
        if (typeof project.gallery === "string") {
          galleryData = safeJSONParse(project.gallery, []);
        } else if (Array.isArray(project.gallery)) {
          galleryData = project.gallery;
        }
      }

      console.log("Raw gallery data:", galleryData);

      galleryData.forEach((galleryGroup, groupIndex) => {
        if (!galleryGroup) return;

        const groupName = galleryGroup.name || `Gallery ${groupIndex + 1}`;
        const groupId = galleryGroup.id || generateId();

        const images = galleryGroup.images || [];
        images.forEach((image, imgIndex) => {
          const imagePath =
            typeof image === "string" ? image : image?.url || image;
          if (!imagePath) return;

          const fullUrl = imagePath.startsWith("http")
            ? imagePath
            : imagePath
              ? `${ImgURL}${imagePath}`
              : "";

          parsedGallery.push({
            id: groupId,
            name: groupName,
            image: imagePath,
            isNew: false,
            isFile: false,
            url: fullUrl,
            preview: fullUrl,
          });
        });
      });
    } catch (error) {
      console.error("Error parsing gallery:", error);
    }

    setFloorplanNames(parsedFloorplans);
    setGalleryNames(parsedGallery);

    setFloorplanFiles([]);
    setGalleryFiles([]);
    setCoverFile(null);
    setBannerFile(null);
    setHomeImageFile(null);
    setCurrentBatchName("Gallery 1");
    setCurrentBatchFiles([]);

    setShowModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      category_id: "",
      project_name: "",
      project_caption: "",
      project_overview: "",
      location: "",
      get_direction: "",
      project_status: "",
      project_process_stage: "",
      amenities: "[]",
      specifications: "[]",
      highlights: "[]",
      nearby_locations: "[]",
      area_statement: "[]",
      specification_details: "[]",
      payment_schedule: "[]",
      highcontent: "[]",
      rank: "",
      brochure: "",
      video: "",
    });

    if (coverPreview) URL.revokeObjectURL(coverPreview);
    if (bannerPreview) URL.revokeObjectURL(bannerPreview);
    if (homeImagePreview) URL.revokeObjectURL(homeImagePreview);
    if (brochurePreview && !brochurePreview.isExisting) {
      URL.revokeObjectURL(brochurePreview.url);
    }

    floorplanNames.forEach((fp) => {
      if (fp.preview && fp.isNew) {
        URL.revokeObjectURL(fp.preview);
      }
    });

    galleryNames.forEach((g) => {
      if (g.preview && g.isNew) {
        URL.revokeObjectURL(g.preview);
      }
    });

    setCoverPreview("");
    setBannerPreview("");
    setHomeImagePreview("");
    setBrochurePreview(null);
    setCoverFile(null);
    setBannerFile(null);
    setHomeImageFile(null);
    setBrochureFile(null);
    setFloorplanFiles([]);
    setGalleryFiles([]);
    setFloorplanNames([]);
    setGalleryNames([]);
    setHighlightInput("");
    setHighlightType("text");
    setCurrentBatchName("Gallery 1");
    setCurrentBatchFiles([]);
    setEditingProject(null);
    setDeleteFloorplans([]);
    setDeleteGalleryBlocks([]);
    setDeleteGalleryImages([]);
    setDeleteHighcontent([]);
    setDeleteBrochure(false);

    setAreaStatement([]);
    setSpecificationDetails([]);
    setPaymentSchedule([]);
    setHighcontent([]);

    if (formUpdateTimeoutRef.current) {
      clearTimeout(formUpdateTimeoutRef.current);
    }
  };

  // Validate form
  const validateForm = () => {
    if (!formData.category_id) {
      toast.error("❌ Please select a category");
      return false;
    }
    if (!formData.project_name?.trim()) {
      toast.error("❌ Please enter project name");
      return false;
    }
    if (!formData.location?.trim()) {
      toast.error("❌ Please enter location");
      return false;
    }
    if (!formData.project_status) {
      toast.error("❌ Please select project status");
      return false;
    }

    return true;
  };

  // Handle form submission - UPDATED FOR VIDEO NULL
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const formDataToSend = new FormData();

      // Basic fields - video removed from here
      const basicFields = [
        "category_id",
        "project_name",
        "project_caption",
        "project_overview",
        "location",
        "get_direction",
        "project_status",
        "project_process_stage",
        "rank",
        "brochure",
      ];

      basicFields.forEach((field) => {
        if (formData[field] !== undefined && formData[field] !== null) {
          formDataToSend.append(field, formData[field]);
        }
      });

      // Handle video URL separately - send null if empty
      const videoValue = formData.video?.trim() || "";
      if (videoValue === "") {
        // Send actual null value for empty video
        formDataToSend.append("video", null);
      } else {
        formDataToSend.append("video", videoValue);
      }

      if (deleteBrochure) {
        formDataToSend.append("delete_brochure", "true");
      }

      const arrayFields = [
        "amenities",
        "specifications",
        "nearby_locations",
        "area_statement",
        "specification_details",
        "payment_schedule",
        "highcontent",
      ];

      arrayFields.forEach((field) => {
        try {
          if (formData[field]) {
            const parsed = safeJSONParse(formData[field], []);
            formDataToSend.append(field, JSON.stringify(parsed));
          } else {
            formDataToSend.append(field, "[]");
          }
        } catch (error) {
          console.error(`Error parsing ${field}:`, error);
          formDataToSend.append(field, "[]");
        }
      });

      try {
        const highlights = getParsedHighlights();
        const formattedHighlights = highlights.map((h) => ({
          id: h.id || generateId(),
          text: h.text || h.value || "",
          type: h.type || "text",
          value: h.value || h.text || "",
        }));
        formDataToSend.append(
          "highlights",
          JSON.stringify(formattedHighlights),
        );
      } catch (error) {
        console.error("Error formatting highlights:", error);
        formDataToSend.append("highlights", "[]");
      }

      if (editingProject && deleteHighcontent.length > 0) {
        formDataToSend.append(
          "delete_highcontent",
          JSON.stringify(deleteHighcontent),
        );
      }

      if (coverFile) formDataToSend.append("cover_image", coverFile);
      if (bannerFile) formDataToSend.append("banner_image", bannerFile);
      if (homeImageFile) formDataToSend.append("home_images", homeImageFile);
      if (brochureFile) formDataToSend.append("brochure", brochureFile);

      const floorplanData = [];
      const newFloorplanFiles = [];

      const uniqueFloorplans = new Set();

      floorplanNames.forEach((fp, index) => {
        if (deleteFloorplans.includes(fp.id)) {
          return;
        }

        const uniqueKey = `${fp.id}-${fp.name}`;

        if (uniqueFloorplans.has(uniqueKey)) {
          console.warn("Duplicate floorplan skipped:", fp.name);
          return;
        }

        uniqueFloorplans.add(uniqueKey);

        const floorplanItem = {
          id: fp.id || generateId(),
          name: fp.name || `Floorplan ${index + 1}`,
        };

        if (fp.isNew && fp.image instanceof File) {
          floorplanItem.tempName = fp.image.name;
          newFloorplanFiles.push(fp.image);
        } else if (!fp.isNew) {
          floorplanItem.image = fp.image;
        }

        floorplanData.push(floorplanItem);
      });

      if (floorplanData.length > 0) {
        formDataToSend.append("floorplan_names", JSON.stringify(floorplanData));
      }

      newFloorplanFiles.forEach((file) => {
        formDataToSend.append("floorplans", file);
      });

      if (deleteFloorplans.length > 0) {
        formDataToSend.append(
          "delete_floorplans",
          JSON.stringify(deleteFloorplans),
        );
      }

      const galleryGroups = {};
      const newGalleryFiles = [];

      galleryNames.forEach((galleryItem) => {
        if (deleteGalleryBlocks.includes(galleryItem.id)) {
          return;
        }

        const isMarkedForDeletion =
          !galleryItem.isNew &&
          deleteGalleryImages.some(
            (d) =>
              d.gid === galleryItem.id && d.images.includes(galleryItem.url),
          );

        if (isMarkedForDeletion) {
          return;
        }

        const groupId = galleryItem.id;
        const groupName =
          galleryItem.name ||
          `Gallery ${Math.floor(galleryNames.indexOf(galleryItem) / 10) + 1}`;

        if (!galleryGroups[groupId]) {
          galleryGroups[groupId] = {
            id: groupId,
            name: groupName,
            total_images: 0,
          };
        }

        galleryGroups[groupId].total_images++;

        if (galleryItem.isNew && galleryItem.image instanceof File) {
          galleryItem.tempName = galleryItem.image.name;
          newGalleryFiles.push(galleryItem.image);
        }
      });

      const galleryData = Object.values(galleryGroups);

      formDataToSend.append("gallery_names", JSON.stringify(galleryData));

      newGalleryFiles.forEach((file) => {
        formDataToSend.append("gallery", file);
      });

      if (deleteGalleryBlocks.length > 0) {
        formDataToSend.append(
          "delete_gallery",
          JSON.stringify(deleteGalleryBlocks),
        );
      }

      if (deleteGalleryImages.length > 0) {
        formDataToSend.append(
          "delete_gallery_images",
          JSON.stringify(deleteGalleryImages),
        );
      }

      // Debug: Log what's being sent
      console.log("Video URL being sent:", formData.video || "null");

      let response;
      const apiUrl = editingProject
        ? `${API_BASE}/edit/project/${editingProject.pid || editingProject.id}`
        : `${API_BASE}/add/project`;

      console.log("Sending request to:", apiUrl);

      response = await axios({
        method: editingProject ? "put" : "post",
        url: apiUrl,
        data: formDataToSend,
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
        timeout: 60000,
      });

      console.log("Response:", response.data);

      if (response.data.success) {
        toast.success(
          `✅ Project ${editingProject ? "updated" : "added"} successfully!`,
        );
        setShowModal(false);
        resetForm();
        fetchAllData();
      } else {
        toast.error(
          `❌ ${response.data.error || response.data.message || "Error saving project"}`,
        );
      }
    } catch (error) {
      console.error("Error saving project:", error);
      const errorMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Error saving project";
      toast.error(`❌ ${errorMsg}`);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Get file icon based on type
  const getFileIcon = (fileType) => {
    if (fileType.includes("pdf")) return "📄";
    if (fileType.includes("word") || fileType.includes("document")) return "📝";
    if (fileType.includes("excel") || fileType.includes("sheet")) return "📊";
    return "📎";
  };

  // View project with null check
  const handleView = (project) => {
    if (!project) {
      toast.error("❌ Invalid project data");
      return;
    }
    setViewingProject(project);
    setShowViewModal(true);
  };

  // Helper functions
  const getStatusConfig = (status) => {
    const statusObj =
      projectStatuses.find((s) => s.id === status) || projectStatuses[0];
    return {
      name: statusObj.name,
      color: statusObj.color,
      percentage: statusObj.percentage,
      background: `${statusObj.color}20`,
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  // Filter projects
  const filteredProjects = useMemo(() => {
    return projects.filter((project) => {
      if (!project) return false;

      const matchesSearch =
        project.project_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        project.location?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        filterStatus === "all" || project.project_status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [projects, searchTerm, filterStatus]);

  // Statistics
  const stats = {
    total: projects.length,
    planning: projects.filter((p) => p && p.project_status === "planning")
      .length,
    construction: projects.filter(
      (p) => p && p.project_status === "construction",
    ).length,
    completed: projects.filter((p) => p && p.project_status === "completed")
      .length,
  };

  // Get parsed arrays for display
  const getParsedArray = (jsonString) => {
    try {
      const parsed = safeJSONParse(jsonString, []);
      return parsed.map((item) => Number(item)).filter((num) => !isNaN(num));
    } catch (error) {
      console.error("Error in getParsedArray:", error);
      return [];
    }
  };

  // Check if an item is selected
  const isItemSelected = (type, itemId) => {
    try {
      const jsonString = formData[type] || "[]";
      const parsedArray = safeJSONParse(jsonString, []);

      return parsedArray.some((item) => {
        if (typeof item === "object" && item !== null) {
          return String(item.id) === String(itemId);
        }
        return String(item) === String(itemId);
      });
    } catch (error) {
      console.error("Error in isItemSelected:", error);
      return false;
    }
  };

  // Get gallery groups for display
  const getGalleryGroups = () => {
    const groups = {};
    galleryNames.forEach((item) => {
      const groupKey = item.id;
      if (!groups[groupKey]) {
        groups[groupKey] = {
          name: item.name,
          id: item.id,
          items: [],
        };
      }
      groups[groupKey].items.push(item);
    });
    return Object.values(groups);
  };

  // Calculate total gallery images
  const getTotalGalleryImages = () => {
    return galleryNames.length;
  };

  // Calculate remaining images after deletions
  const getRemainingImagesCount = () => {
    const remainingExistingImages = galleryNames.filter((item) => {
      if (item.isNew) return false;

      if (deleteGalleryBlocks.includes(item.id)) return false;

      const isMarkedForDeletion = deleteGalleryImages.some(
        (d) => d.gid === item.id && d.images.includes(item.url),
      );

      return !isMarkedForDeletion;
    }).length;

    const newImagesCount = galleryNames.filter((item) => item.isNew).length;

    return remainingExistingImages + newImagesCount;
  };

  // Check if an existing image is marked for deletion
  const isImageMarkedForDeletion = (galleryItem) => {
    if (galleryItem.isNew) return false;

    if (deleteGalleryBlocks.includes(galleryItem.id)) return true;

    return deleteGalleryImages.some(
      (d) => d.gid === galleryItem.id && d.images.includes(galleryItem.url),
    );
  };

  // Check if an existing floorplan is marked for deletion
  const isFloorplanMarkedForDeletion = (floorplan) => {
    if (floorplan.isNew) return false;
    return deleteFloorplans.includes(floorplan.id);
  };

  // Handle select all
  const handleSelectAll = (type, allIds) => {
    setFormData((prev) => ({
      ...prev,
      [type]: JSON.stringify(
        allIds.map((id) => Number(id)).filter((id) => !isNaN(id)),
      ),
    }));
  };

  // Handle clear all
  const handleClearAll = (type) => {
    setFormData((prev) => ({
      ...prev,
      [type]: JSON.stringify([]),
    }));
  };

  // Render highlights in view modal
  const renderHighlights = (project) => {
    if (!project) return [];

    let highlights = [];

    try {
      if (project.highlights) {
        const parsedHighlights = parseHighlightsFromDB(project);
        highlights = parsedHighlights.map((item) => item.value || item.text);
      }
    } catch (error) {
      console.error("Error rendering highlights:", error);
    }

    return highlights;
  };

  // Render project overview with HTML
  const renderProjectOverview = (htmlContent) => {
    return (
      <div
        className="project-overview-content"
        dangerouslySetInnerHTML={{
          __html: htmlContent || "No overview provided.",
        }}
      />
    );
  };

  return (
    <div className="project-management-container">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="header-container">
        <div>
          <h1 className="main-title">Project Management</h1>
          <p className="subtitle">
            Manage all your construction and real estate projects
          </p>
        </div>
        <div className="header-buttons">
          <button
            onClick={refreshData}
            className="refresh-btn"
            title="Refresh Data"
          >
            <FiRefreshCw /> Refresh
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="add-project-btn"
          >
            <FiPlus style={{ fontSize: "18px" }} />
            Add New Project
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div
            className="stat-icon"
            style={{
              background: "linear-gradient(135deg, #667eea, #667eea99)",
            }}
          >
            <FiHome />
          </div>
          <div>
            <h3 className="stat-number">{stats.total}</h3>
            <p className="stat-label">Total Projects</p>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{
              background: "linear-gradient(135deg, #f59e0b, #f59e0b99)",
            }}
          >
            <FiSettings />
          </div>
          <div>
            <h3 className="stat-number">{stats.planning}</h3>
            <p className="stat-label">Planning</p>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{
              background: "linear-gradient(135deg, #8b5cf6, #8b5cf699)",
            }}
          >
            <FiBarChart />
          </div>
          <div>
            <h3 className="stat-number">{stats.construction}</h3>
            <p className="stat-label">Under Construction</p>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{
              background: "linear-gradient(135deg, #10b981, #10b98199)",
            }}
          >
            <FiCheck />
          </div>
          <div>
            <h3 className="stat-number">{stats.completed}</h3>
            <p className="stat-label">Completed</p>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="search-filter-container">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search projects by name or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-controls">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="status-filter"
          >
            <option value="all">All Status</option>
            {projectStatuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => {
              setSearchTerm("");
              setFilterStatus("all");
            }}
            className="clear-filters-btn"
          >
            <FiX /> Clear Filters
          </button>
        </div>
      </div>

      {/* Projects Table */}
      <div className="projects-table-container">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading projects...</p>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="empty-state">
            <FiHome className="empty-icon" />
            <h3>No projects found</h3>
            <p>Try adjusting your search or add a new project</p>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="add-first-project-btn"
            >
              <FiPlus /> Add Your First Project
            </button>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Project Details</th>
                  <th>Location</th>
                  <th>Status & Progress</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => {
                  if (!project) return null;

                  const statusConfig = getStatusConfig(project.project_status);
                  const category = categories.find(
                    (c) => c.id == project.category_id,
                  );

                  return (
                    <tr key={project.pid || project.id}>
                      <td>
                        <div className="project-details">
                          {project.cover_image ? (
                            <img
                              src={
                                project.cover_image.startsWith("http")
                                  ? project.cover_image
                                  : `${ImgURL}${project.cover_image}`
                              }
                              alt={project.project_name}
                              className="project-image"
                            />
                          ) : (
                            <div className="image-placeholder">
                              <FiImage />
                            </div>
                          )}
                          <div>
                            <h4
                              className="project-name"
                              onClick={() => handleView(project)}
                            >
                              {project.project_name}
                            </h4>
                            <p className="project-caption">
                              {project.project_caption || "No caption"}
                            </p>
                            <div className="project-meta">
                              <span>
                                <FiCalendar /> {formatDate(project.created_at)}
                              </span>
                              <span>
                                <FiTag /> {category?.name || "Unknown"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="location-details">
                          <FiMapPin className="location-icon" />
                          <div>
                            <p className="location-text">{project.location}</p>
                            {project.get_direction && (
                              <a
                                href={project.get_direction}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="directions-link"
                              >
                                <FiNavigation /> Get Directions
                              </a>
                            )}
                          </div>
                        </div>
                      </td>

                      <td>
                        <div>
                          <div
                            className="status-badge"
                            style={{
                              background: `${statusConfig.color}20`,
                              color: statusConfig.color,
                              border: `1px solid ${statusConfig.color}40`,
                            }}
                          >
                            <FiTrendingUp />
                            {statusConfig.name}
                          </div>
                          <div className="progress-container">
                            <div className="progress-labels">
                              <span>Progress</span>
                              <span style={{ color: statusConfig.color }}>
                                {statusConfig.percentage}%
                              </span>
                            </div>
                            <div className="progress-bar">
                              <div
                                className="progress-fill"
                                style={{
                                  width: `${statusConfig.percentage}%`,
                                  background: statusConfig.color,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleView(project)}
                            className="view-btn"
                            title="View Details"
                          >
                            <FiEye /> View
                          </button>
                          <button
                            onClick={() => handleEdit(project)}
                            className="edit-btn"
                            title="Edit"
                          >
                            <FiEdit /> Edit
                          </button>
                          <button
                            onClick={() => showDeleteConfirmation(project)}
                            className="delete-btn"
                            title="Delete"
                          >
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && projectToDelete && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <div className="delete-modal-header">
              <FiAlertTriangle className="warning-icon" />
              <h2>Confirm Deletion</h2>
            </div>

            <div className="delete-modal-body">
              <p>Are you sure you want to delete the project:</p>
              <p className="project-to-delete">
                "{projectToDelete.project_name}"
              </p>
              <p className="warning-text">
                ⚠️ This action cannot be undone. All project data including
                images will be permanently deleted.
              </p>

              <div className="delete-modal-actions">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setProjectToDelete(null);
                  }}
                  className="cancel-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleDelete(projectToDelete.pid || projectToDelete.id)
                  }
                  className="confirm-delete-btn"
                >
                  <FiTrash2 /> Delete Project
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Project Modal */}
      {showViewModal && viewingProject && (
        <div className="modal-overlay">
          <div className="view-modal">
            <div className="modal-header">
              <h2>Project Details</h2>
              <button
                onClick={() => setShowViewModal(false)}
                className="close-modal-btn"
              >
                &times;
              </button>
            </div>

            <div className="modal-content">
              <div className="view-project-header">
                {viewingProject.cover_image && (
                  <img
                    src={
                      viewingProject.cover_image.startsWith("http")
                        ? viewingProject.cover_image
                        : `${ImgURL}${viewingProject.cover_image}`
                    }
                    alt={viewingProject.project_name}
                    className="view-project-image"
                  />
                )}
                <div>
                  <h3>{viewingProject.project_name}</h3>
                  <p>{viewingProject.project_caption}</p>
                  <div className="view-project-meta">
                    <span>
                      <FiMapPin /> {viewingProject.location}
                    </span>
                    <span>
                      <FiCalendar /> {formatDate(viewingProject.created_at)}
                    </span>
                    {viewingProject.rank && (
                      <span className="view-rank">
                        <FiAward /> Rank: {viewingProject.rank}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {(viewingProject.brochure || viewingProject.video) && (
                <div className="media-links-section">
                  {viewingProject.brochure && (
                    <a
                      href={
                        viewingProject.brochure.startsWith("http")
                          ? viewingProject.brochure
                          : `${ImgURL}${viewingProject.brochure}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="media-link"
                    >
                      <FiFile /> Download Brochure
                    </a>
                  )}
                  {viewingProject.video && (
                    <a
                      href={viewingProject.video}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="media-link video-link"
                    >
                      <FiVideo /> Watch Video
                    </a>
                  )}
                </div>
              )}

              <div className="project-overview">
                <h4>Project Overview</h4>
                {renderProjectOverview(viewingProject.project_overview)}
              </div>

              {renderHighlights(viewingProject).length > 0 && (
                <div className="project-overview">
                  <h4>Project Highlights</h4>
                  <div className="highlights-list">
                    {renderHighlights(viewingProject).map(
                      (highlight, index) => (
                        <div key={index} className="highlight-item">
                          <div className="highlight-content">
                            <FiStar className="highlight-icon" />
                            <span className="highlight-text">{highlight}</span>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    handleEdit(viewingProject);
                  }}
                  className="edit-from-view-btn"
                >
                  <FiEdit /> Edit Project
                </button>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Project Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="project-modal">
            <div className="modal-header">
              <h2>{editingProject ? "Edit Project" : "Add New Project"}</h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="close-modal-btn"
              >
                &times;
              </button>
            </div>

            {/* Tabs */}
            <div className="modal-tabs">
              {["basic", "features", "additional", "media"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                >
                  {tab === "basic"
                    ? "Basic Info"
                    : tab === "features"
                      ? "Features & Amenities"
                      : tab === "additional"
                        ? "Additional Details"
                        : "Media & Gallery"}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="tab-content">
                {/* Basic Info Tab */}
                {activeTab === "basic" && (
                  <div className="basic-info-grid">
                    {/* Category */}
                    <div className="form-group">
                      <label>Category *</label>
                      <select
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleInputChange}
                        required
                        className="form-select"
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name || cat.category_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Project Name */}
                    <div className="form-group">
                      <label>Project Name *</label>
                      <input
                        type="text"
                        name="project_name"
                        value={formData.project_name}
                        onChange={handleInputChange}
                        placeholder="Enter project name"
                        required
                        className="form-input"
                      />
                    </div>

                    {/* Project Caption */}
                    <div className="form-group full-width">
                      <label>Project Caption</label>
                      <input
                        type="text"
                        name="project_caption"
                        value={formData.project_caption}
                        onChange={handleInputChange}
                        placeholder="Brief project description"
                        className="form-input"
                      />
                    </div>

                    {/* Location */}
                    <div className="form-group">
                      <label>Location *</label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        placeholder="Enter project location"
                        required
                        className="form-input"
                      />
                    </div>

                    {/* Get Direction URL */}
                    <div className="form-group">
                      <label>Google Maps URL</label>
                      <input
                        type="url"
                        name="get_direction"
                        value={formData.get_direction}
                        onChange={handleInputChange}
                        placeholder="https://maps.google.com/..."
                        className="form-input"
                      />
                    </div>

                    {/* Project Status */}
                    <div className="form-group">
                      <label>Project Status *</label>
                      <select
                        name="project_status"
                        value={formData.project_status}
                        onChange={handleInputChange}
                        required
                        className="form-select"
                      >
                        <option value="">Select Status</option>
                        {Array.from({ length: 19 }, (_, i) => {
                          const percentage = (i + 1) * 5; // 10, 28, 46, 64, 82 (or adjust for even steps)
                          return (
                            <option key={percentage} value={percentage}>
                              {percentage}%
                            </option>
                          );
                        })}
                        <option value={100}>100%</option>{" "}
                        {/* Ensure 100% is included */}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Property Location Map Link</label>
                      <input
                        type="text"
                        name="project_process_stage"
                        value={formData.project_process_stage}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter Google Map Link"
                      />
                    </div>

                    {/* Rank */}
                    <div className="form-group">
                      <label>Rank (Optional)</label>
                      <input
                        type="number"
                        name="rank"
                        value={formData.rank}
                        onChange={handleInputChange}
                        placeholder="Enter rank number"
                        min="1"
                        className="form-input"
                      />
                      <small className="form-help">
                        Lower number = higher priority
                      </small>
                    </div>

                    {/* Video URL - FIXED */}
                    <div className="form-group full-group">
                      <label>Video URL</label>
                      <div style={{ position: "relative" }}>
                        <input
                          type="url"
                          name="video"
                          value={formData.video || ""}
                          onChange={(e) => {
                            setFormData((prev) => ({
                              ...prev,
                              video: e.target.value,
                            }));
                          }}
                          placeholder="https://youtube.com/... or https://vimeo.com/..."
                          className="form-input"
                          style={{ paddingRight: "40px" }}
                        />
                        {formData.video && formData.video.trim() !== "" && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormData((prev) => ({ ...prev, video: "" }));
                              toast.info(
                                "Video URL cleared. Save to remove video.",
                              );
                            }}
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "transparent",
                              border: "none",
                              color: "#ef4444",
                              cursor: "pointer",
                              fontSize: "18px",
                              padding: "0",
                            }}
                            title="Clear video URL"
                          >
                            <FiX />
                          </button>
                        )}
                      </div>
                      <small className="form-help">
                        Enter YouTube or Vimeo video link. Clear and save to
                        remove existing video.
                      </small>
                      {formData.video && formData.video.trim() !== "" && (
                        <div style={{ marginTop: "8px" }}>
                          <a
                            href={formData.video}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "#3b82f6",
                              textDecoration: "none",
                              fontSize: "14px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <FiVideo /> Preview Video Link
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Project Overview - RICH TEXT EDITOR */}
                    <div className="form-group full-width">
                      <label>Project Overview</label>
                      <RichTextEditor
                        value={formData.project_overview}
                        onChange={(value) =>
                          setFormData((prev) => ({
                            ...prev,
                            project_overview: value,
                          }))
                        }
                        placeholder="Enter detailed project description..."
                        rows={8}
                      />
                      <small className="form-help">
                        Use formatting buttons for rich text editing. HTML tags
                        will be displayed as formatted text.
                      </small>
                    </div>
                  </div>
                )}

                {/* Features & Amenities Tab */}
                {activeTab === "features" && (
                  <div className="features-container">
                    {/* Amenities */}
                    <div className="feature-section">
                      <label className="feature-section-title">
                        Select Amenities
                        <span className="selected-count">
                          (Selected: {getParsedArray(formData.amenities).length}
                          )
                        </span>
                      </label>

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginBottom: "20px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            handleSelectAll(
                              "amenities",
                              amenities.map((a) => a.amid),
                            )
                          }
                          className="refresh-btn"
                          style={{ padding: "8px 16px", fontSize: "14px" }}
                        >
                          <FiCheck /> Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => handleClearAll("amenities")}
                          className="clear-filters-btn"
                          style={{ padding: "8px 16px", fontSize: "14px" }}
                        >
                          <FiX /> Clear All
                        </button>
                        <div
                          style={{
                            marginLeft: "auto",
                            fontSize: "14px",
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <FiFilter />
                          <span>
                            {getParsedArray(formData.amenities).length} selected
                          </span>
                        </div>
                      </div>

                      <div className="checkbox-grid">
                        {amenities.map((amenity) => {
                          const isChecked = isItemSelected(
                            "amenities",
                            amenity.amid,
                          );

                          return (
                            <label
                              key={amenity.amid}
                              className={`checkbox-label ${isChecked ? "checked" : ""}`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() =>
                                  handleFeatureToggle("amenities", amenity.amid)
                                }
                                className="checkbox-input"
                              />
                              <span className="checkbox-text">
                                {amenity.amenities_name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Specifications */}
                    <div className="feature-section">
                      <label className="feature-section-title">
                        Select Specifications
                        <span className="selected-count">
                          (Selected:{" "}
                          {getParsedArray(formData.specifications).length})
                        </span>
                      </label>

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginBottom: "20px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            handleSelectAll(
                              "specifications",
                              specifications.map((s) => s.sid),
                            )
                          }
                          className="refresh-btn"
                          style={{ padding: "8px 16px", fontSize: "14px" }}
                        >
                          <FiCheck /> Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => handleClearAll("specifications")}
                          className="clear-filters-btn"
                          style={{ padding: "8px 16px", fontSize: "14px" }}
                        >
                          <FiX /> Clear All
                        </button>
                        <div
                          style={{
                            marginLeft: "auto",
                            fontSize: "14px",
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <FiFilter />
                          <span>
                            {getParsedArray(formData.specifications).length}{" "}
                            selected
                          </span>
                        </div>
                      </div>

                      <div className="checkbox-grid">
                        {specifications.map((spec) => {
                          const isChecked = isItemSelected(
                            "specifications",
                            spec.sid,
                          );
                          return (
                            <label
                              key={spec.sid}
                              className={`checkbox-label ${isChecked ? "checked" : ""}`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() =>
                                  handleFeatureToggle(
                                    "specifications",
                                    spec.sid,
                                  )
                                }
                                className="checkbox-input"
                              />
                              <span className="checkbox-text">
                                {spec.specification_name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    {/* Nearby Locations */}
                    <div className="feature-section">
                      <label className="feature-section-title">
                        Select Nearby Locations
                        <span className="selected-count">
                          (Selected:{" "}
                          {getParsedArray(formData.nearby_locations).length})
                        </span>
                      </label>

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginBottom: "20px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            handleSelectAll(
                              "nearby_locations",
                              nearbyLocations.map((n) => n.nid),
                            )
                          }
                          className="refresh-btn"
                          style={{ padding: "8px 16px", fontSize: "14px" }}
                        >
                          <FiCheck /> Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => handleClearAll("nearby_locations")}
                          className="clear-filters-btn"
                          style={{ padding: "8px 16px", fontSize: "14px" }}
                        >
                          <FiX /> Clear All
                        </button>
                        <div
                          style={{
                            marginLeft: "auto",
                            fontSize: "14px",
                            color: "#64748b",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <FiFilter />
                          <span>
                            {getParsedArray(formData.nearby_locations).length}{" "}
                            selected
                          </span>
                        </div>
                      </div>

                      <div className="checkbox-grid">
                        {nearbyLocations.map((location) => {
                          const isChecked = isItemSelected(
                            "nearby_locations",
                            location.nid,
                          );
                          return (
                            <label
                              key={location.nid}
                              className={`checkbox-label ${isChecked ? "checked" : ""}`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() =>
                                  handleFeatureToggle(
                                    "nearby_locations",
                                    location.nid,
                                  )
                                }
                                className="checkbox-input"
                              />
                              <span className="checkbox-text">
                                {location.nearby_location_name}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Details Tab */}
                {activeTab === "additional" && (
                  <AdditionalDetailsTab
                    areaStatement={areaStatement}
                    specificationDetails={specificationDetails}
                    paymentSchedule={paymentSchedule}
                    highcontent={highcontent}
                    onUpdateAreaStatement={updateAreaStatement}
                    onUpdateSpecificationDetail={updateSpecificationDetail}
                    onUpdatePaymentSchedule={updatePaymentSchedule}
                    onUpdateHighcontent={updateHighcontent}
                    onRemoveAreaStatement={removeAreaStatementRow}
                    onRemoveSpecificationDetail={removeSpecificationDetail}
                    onRemovePaymentSchedule={removePaymentScheduleRow}
                    onRemoveHighcontent={removeHighcontentRow}
                    onAddAreaStatement={addAreaStatementRow}
                    onAddSpecificationDetail={addSpecificationDetail}
                    onAddPaymentSchedule={addPaymentScheduleRow}
                    onAddHighcontent={addHighcontentRow}
                  />
                )}

                {/* Media & Gallery Tab */}
                {activeTab === "media" && (
                  <div className="media-container">
                    {/* Cover Image */}
                    <div className="media-section">
                      <label className="media-label">Cover Image *</label>
                      <div className="media-upload">
                        {coverPreview ? (
                          <div className="image-preview-container">
                            <img
                              src={coverPreview}
                              alt="Cover preview"
                              className="image-preview"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setCoverPreview("");
                                setCoverFile(null);
                              }}
                              className="remove-image-btn"
                            >
                              <FiX />
                            </button>
                          </div>
                        ) : (
                          <label className="upload-area">
                            <FiUpload className="upload-icon" />
                            <span className="upload-text">Upload Cover</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileUpload(e, "cover_image")
                              }
                              className="file-input"
                            />
                          </label>
                        )}
                        <div className="upload-info">
                          <p>Main project image (Recommended: 1200×800)</p>
                          <p>
                            This will be displayed as the main project thumbnail
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Banner Image */}
                    <div className="media-section">
                      <label className="media-label">Banner Image</label>
                      <div className="media-upload">
                        {bannerPreview ? (
                          <div className="image-preview-container">
                            <img
                              src={bannerPreview}
                              alt="Banner preview"
                              className="image-preview"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setBannerPreview("");
                                setBannerFile(null);
                              }}
                              className="remove-image-btn"
                            >
                              <FiX />
                            </button>
                          </div>
                        ) : (
                          <label className="upload-area">
                            <FiUpload className="upload-icon" />
                            <span className="upload-text">Upload Banner</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileUpload(e, "banner_image")
                              }
                              className="file-input"
                            />
                          </label>
                        )}
                        <div className="upload-info">
                          <p>Project banner image (Recommended: 1920×400)</p>
                          <p>Displayed at the top of project details page</p>
                        </div>
                      </div>
                    </div>

                    {/* Home Card Image */}
                    <div className="media-section">
                      <label className="media-label">Home Card Image *</label>
                      <div className="media-upload">
                        {homeImagePreview ? (
                          <div className="image-preview-container">
                            <img
                              src={homeImagePreview}
                              alt="Home card preview"
                              className="image-preview"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setHomeImagePreview("");
                                setHomeImageFile(null);
                              }}
                              className="remove-image-btn"
                            >
                              <FiX />
                            </button>
                          </div>
                        ) : (
                          <label className="upload-area">
                            <FiUpload className="upload-icon" />
                            <span className="upload-text">
                              Upload Home Card
                            </span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) =>
                                handleFileUpload(e, "home_images")
                              }
                              className="file-input"
                            />
                          </label>
                        )}
                        <div className="upload-info">
                          <p>Home page card image (Recommended: 400×300)</p>
                          <p>
                            This will be displayed on home page project cards
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Brochure Upload */}
                    <div className="media-section">
                      <label className="media-label">Brochure Document</label>
                      <div className="media-upload">
                        {brochurePreview ? (
                          <div className="brochure-preview">
                            <div className="brochure-info">
                              <div className="brochure-icon">
                                {getFileIcon(brochurePreview.type || "")}
                              </div>
                              <div>
                                <p className="brochure-name">
                                  {brochurePreview.name}
                                </p>
                                {brochurePreview.size && (
                                  <p className="brochure-size">
                                    {formatFileSize(brochurePreview.size)}
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="brochure-actions">
                              {brochurePreview.url && (
                                <a
                                  href={brochurePreview.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="view-brochure-btn"
                                  title="View/Download"
                                >
                                  <FiEye />
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={removeBrochure}
                                className="remove-brochure-btn"
                                title="Remove brochure"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                            {deleteBrochure && (
                              <div className="delete-badge">
                                Will be deleted on save
                              </div>
                            )}
                          </div>
                        ) : (
                          <label className="upload-area brochure-upload">
                            <FiUpload className="upload-icon" />
                            <span className="upload-text">Upload Brochure</span>
                            <span className="upload-hint">
                              PDF, DOC, DOCX, XLS, XLSX
                            </span>
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                              onChange={(e) => handleFileUpload(e, "brochure")}
                              className="file-input"
                            />
                          </label>
                        )}
                        <div className="upload-info">
                          <p>Project brochure document (max 10MB)</p>
                          <p>Visitors can download this brochure</p>
                        </div>
                      </div>
                    </div>

                    {/* Floorplans */}
                    <div className="media-section">
                      <div className="media-header">
                        <label className="media-label">
                          Floorplans
                          {editingProject && (
                            <span className="edit-hint">
                              (Click on existing floorplans to remove them)
                            </span>
                          )}
                        </label>
                        <label className="upload-btn">
                          <FiPlus /> Add Floorplans
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => handleFileUpload(e, "floorplans")}
                            className="file-input"
                          />
                        </label>
                      </div>

                      {floorplanNames.length > 0 ? (
                        <div className="floorplan-grid">
                          {floorplanNames.map((floorplan, index) => {
                            const displayName =
                              floorplan.name && floorplan.name.trim() !== ""
                                ? floorplan.name
                                : `Floorplan ${index + 1}`;
                            const isMarkedForDeletion =
                              isFloorplanMarkedForDeletion(floorplan);

                            return (
                              <div
                                key={`${floorplan.id}-${index}`}
                                className={`floorplan-item ${isMarkedForDeletion ? "marked-for-deletion" : ""}`}
                              >
                                <img
                                  src={floorplan.preview || floorplan.url}
                                  alt={displayName}
                                  className="floorplan-image"
                                />
                                <div className="floorplan-name-input">
                                  <input
                                    type="text"
                                    value={floorplan.name || ""}
                                    onChange={(e) =>
                                      updateFloorplanName(index, e.target.value)
                                    }
                                    placeholder={`Floorplan ${index + 1}`}
                                    disabled={isMarkedForDeletion}
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeFloorplan(index)}
                                  className="remove-floorplan-btn"
                                  title={
                                    isMarkedForDeletion
                                      ? "Restore floorplan"
                                      : "Remove floorplan"
                                  }
                                >
                                  {isMarkedForDeletion ? <FiCheck /> : <FiX />}
                                </button>
                                {!floorplan.isNew && !isMarkedForDeletion && (
                                  <div className="existing-badge">Existing</div>
                                )}
                                {isMarkedForDeletion && (
                                  <div className="delete-badge">
                                    To be deleted
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="empty-media">
                          <FiFileText className="empty-media-icon" />
                          <p>No floorplans uploaded yet</p>
                          <p>Upload floorplan images with descriptive names</p>
                        </div>
                      )}
                    </div>

                    {/* Gallery Images */}
                    <div className="media-section">
                      <div className="media-header">
                        <label className="media-label">Gallery Images</label>
                        <div
                          className="image-count"
                          style={
                            getRemainingImagesCount() >= 0
                              ? {
                                  color: "#10b981",
                                }
                              : {
                                  color: "#ef4444",
                                }
                          }
                        >
                          {getRemainingImagesCount()} images
                          {editingProject &&
                            (deleteGalleryBlocks.length > 0 ||
                              deleteGalleryImages.length > 0) && (
                              <span className="deletion-count">
                                ({deleteGalleryBlocks.length} blocks,{" "}
                                {deleteGalleryImages.length} images marked for
                                deletion)
                              </span>
                            )}
                        </div>
                      </div>

                      {/* Batch Upload */}
                      <div className="batch-upload">
                        <div className="batch-header">
                          <h4>Batch Upload (Max 10 images per batch)</h4>
                          <div className="batch-count">
                            Current batch: {currentBatchFiles.length} images
                          </div>
                        </div>

                        <div className="batch-controls">
                          <input
                            type="text"
                            value={currentBatchName}
                            onChange={(e) =>
                              setCurrentBatchName(e.target.value)
                            }
                            placeholder="Enter batch name (e.g., Exterior, Interior)"
                            className="batch-name-input"
                          />

                          <div className="batch-buttons">
                            <label className="batch-select-btn">
                              <FiUpload /> Select Images
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) =>
                                  handleFileUpload(e, "gallery_batch")
                                }
                                className="file-input"
                              />
                            </label>

                            <button
                              type="button"
                              onClick={addBatchToGallery}
                              disabled={currentBatchFiles.length === 0}
                              className={`batch-add-btn ${currentBatchFiles.length === 0 ? "disabled" : ""}`}
                            >
                              <FiPlus /> Add to Gallery
                            </button>
                          </div>
                        </div>

                        {/* Current Batch Preview */}
                        {currentBatchFiles.length > 0 && (
                          <div className="batch-preview">
                            <div className="preview-header">
                              <span>
                                Current Batch Preview (
                                {currentBatchFiles.length} images)
                              </span>
                              <button
                                type="button"
                                onClick={() => setCurrentBatchFiles([])}
                                className="clear-batch-btn"
                              >
                                <FiX /> Clear
                              </button>
                            </div>
                            <div className="preview-grid">
                              {currentBatchFiles.map((file, index) => (
                                <div key={index} className="preview-item">
                                  <img
                                    src={URL.createObjectURL(file)}
                                    alt={`Batch ${index + 1}`}
                                    className="preview-image"
                                  />
                                  <span className="preview-number">
                                    {index + 1}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Gallery Groups */}
                      {galleryNames.length > 0 ? (
                        <div className="gallery-groups">
                          <h4>
                            Gallery Groups ({getGalleryGroups().length} groups,{" "}
                            {getTotalGalleryImages()} total images)
                          </h4>

                          {getGalleryGroups().map((group, groupIndex) => {
                            const isBlockMarkedForDeletion =
                              deleteGalleryBlocks.includes(group.id);
                            const imagesInBlockMarkedForDeletion =
                              deleteGalleryImages.find(
                                (d) => d.gid === group.id,
                              );

                            return (
                              <div
                                key={group.id}
                                className={`gallery-group ${isBlockMarkedForDeletion ? "marked-for-deletion" : ""}`}
                              >
                                <div className="group-header">
                                  <div className="group-info">
                                    <span className="group-name">
                                      {group.name}
                                    </span>
                                    <span className="group-count">
                                      {group.items.length} images
                                      {isBlockMarkedForDeletion &&
                                        " (marked for deletion)"}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      deleteGalleryGroup(group.name, group.id)
                                    }
                                    className="delete-group-btn"
                                    title={
                                      isBlockMarkedForDeletion
                                        ? "Restore gallery block"
                                        : "Delete gallery block"
                                    }
                                  >
                                    <FiTrash2 />{" "}
                                    {isBlockMarkedForDeletion
                                      ? "Restore Block"
                                      : "Delete Block"}
                                  </button>
                                </div>

                                <div className="group-images">
                                  {group.items.map((item, itemIndex) => {
                                    const isMarkedForDeletion =
                                      isImageMarkedForDeletion(item);

                                    return (
                                      <div
                                        key={itemIndex}
                                        className={`gallery-item ${isMarkedForDeletion ? "marked-for-deletion" : ""}`}
                                      >
                                        <div className="gallery-image-container">
                                          <img
                                            src={item.preview || item.url}
                                            alt={`${group.name} ${itemIndex + 1}`}
                                            className="gallery-image"
                                          />
                                          <div className="image-overlay">
                                            <span className="image-number">
                                              Image {itemIndex + 1}
                                            </span>
                                            <span className="image-tag">
                                              #{groupIndex + 1}-{itemIndex + 1}
                                            </span>
                                          </div>
                                        </div>
                                        <div className="gallery-item-controls">
                                          <input
                                            type="text"
                                            value={item.name || ""}
                                            onChange={(e) =>
                                              updateGalleryName(
                                                itemIndex,
                                                e.target.value,
                                              )
                                            }
                                            placeholder="Gallery Name"
                                            className="gallery-name-input"
                                            disabled={isMarkedForDeletion}
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              removeGalleryImage(
                                                galleryNames.indexOf(item),
                                              )
                                            }
                                            className="remove-gallery-image-btn"
                                            title={
                                              isMarkedForDeletion
                                                ? "Restore image"
                                                : "Remove image"
                                            }
                                          >
                                            {isMarkedForDeletion ? (
                                              <FiCheck />
                                            ) : (
                                              <FiTrash2 />
                                            )}
                                          </button>
                                        </div>
                                        {!item.isNew &&
                                          !isMarkedForDeletion && (
                                            <div className="existing-badge">
                                              Existing
                                            </div>
                                          )}
                                        {isMarkedForDeletion && (
                                          <div className="delete-badge">
                                            To be deleted
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="empty-gallery">
                          <FiImage className="empty-gallery-icon" />
                          <div>
                            <h4>No gallery images yet</h4>
                            <p>
                              Upload images in batches of up to 10 images each
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <div>
                  {activeTab !== "basic" && (
                    <button
                      type="button"
                      onClick={() => {
                        const tabs = [
                          "basic",
                          "features",
                          "additional",
                          "media",
                        ];
                        const currentIndex = tabs.indexOf(activeTab);
                        setActiveTab(tabs[currentIndex - 1]);
                      }}
                      className="prev-btn"
                    >
                      <FiChevronLeft /> Previous
                    </button>
                  )}
                </div>

                <div className="footer-buttons">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>

                  {activeTab !== "media" ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          const tabs = [
                            "basic",
                            "features",
                            "additional",
                            "media",
                          ];
                          const currentIndex = tabs.indexOf(activeTab);
                          setActiveTab(tabs[currentIndex + 1]);
                        }}
                        className="next-btn"
                      >
                        Next <FiChevronRight />
                      </button>
                      <button type="submit" className="submit-btn">
                        <FiCheck />{" "}
                        {editingProject ? "Update Project" : "Add Project"}
                      </button>
                    </>
                  ) : (
                    <button type="submit" className="submit-btn">
                      <FiCheck />{" "}
                      {editingProject ? "Update Project" : "Add Project"}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagementDataTable;
